import { assertNumberBetween } from '../validators';
import { greaterThanOrEqualWithinEpsilon } from './asset/utils';

export type PlaybackRegion = [number, number];
export class PlaybackRegions {
    /**
     * Validate the params of cut and uncut methods.
     */
    static assertRange(
        start: number,
        end: number,
        assetDuration: number,
        regions: Readonly<Readonly<PlaybackRegion>[]>,
    ) {
        assertNumberBetween(start, 0, assetDuration);
        assertNumberBetween(end, 0, assetDuration);
        assertNumberBetween(assetDuration, 0, Infinity);

        if (regions.length <= 0) {
            throw new Error('Playback regions cannot be empty');
        }

        // out of bounds conditions
        if (start < 0 || end < 0) {
            throw new Error('Cut times cannot be less than 0');
        }
        if (end - start > assetDuration) {
            throw new Error('Cut region cannot be greater than the asset duration');
        }

        // invalid values
        if (end <= start) {
            throw new Error('Cut end time cannot be less than or equal to start time');
        }
    }

    /**
     * Cuts the playback regions at specified start and end times. A cut in the middle of a playback region will split the region into two.
     * For example, if this.regions = [[0, 10000]], cut(2000, 8000) will result in [[0, 2000], [8000, 10000]]
     * Cut times overlap the playback times, and are inclusive.
     */
    static cut(
        cutStart: number,
        cutEnd: number,
        assetDuration: number,
        regions: Readonly<Readonly<PlaybackRegion>[]>,
    ) {
        this.assertRange(cutStart, cutEnd, assetDuration, regions);

        const playbackRegionsAfterCut: PlaybackRegion[] = [];
        for (const [start, end] of regions) {
            // If the playback region is completely before the cut
            if (end <= cutStart) {
                playbackRegionsAfterCut.push([start, end]);
            }
            // If the playback region is completely after the cut
            else if (start >= cutEnd) {
                playbackRegionsAfterCut.push([start, end]);
            }
            // If the playback region overlaps with the cut, split the region
            else {
                // Add the part before the cut
                if (start < cutStart) {
                    playbackRegionsAfterCut.push([start, cutStart]);
                }
                // Add the part after the cut
                if (end > cutEnd) {
                    playbackRegionsAfterCut.push([cutEnd, end]);
                }
            }
            // if start > cutStart and end < cutEnd, then the region is completely inside the cut
            // this region is "cut out", so we don't push it onto the result stack
        }

        if (playbackRegionsAfterCut.length === 0) {
            // don't allow cutting out the entire asset
            throw new Error('Cutting resulted in no playback regions');
        }

        // save the new regions, sorted by start time
        playbackRegionsAfterCut.sort((regionA, regionB) => regionA[0] - regionB[0]);
        return playbackRegionsAfterCut;
    }

    /**
     * Uncut is the reverse of cut. An uncut that doesn't overlap with any playback regions will add a new playback region.
     * For example, if this.regions = [[0, 2000], [8000, 10000]], uncut(5000, 6000) will result in [[0, 2000], [5000, 6000], [8000, 10000]]
     * If the uncut overlaps with existing playback regions, the playback regions will be merged in this method. We defrag regions as we go.
     */
    static uncut(
        uncutStart: number,
        uncutEnd: number,
        assetDuration: number,
        regions: Readonly<Readonly<PlaybackRegion>[]>,
    ) {
        this.assertRange(uncutStart, uncutEnd, assetDuration, regions);

        const playbackRegionsAfterUncut: PlaybackRegion[] = [];
        for (const [start, end] of regions) {
            // keep the playback regions that are completely before/after the added region
            if (end < uncutStart || start > uncutEnd) {
                playbackRegionsAfterUncut.push([start, end]);
            }
            // if an overlap happens, extend the edges of the regions by comparing it with the existing overlapping regions
            // after extending the edges, keep just the extended region since the new region completely includes the old region
            else {
                uncutStart = Math.min(uncutStart, start);
                uncutEnd = Math.max(uncutEnd, end);
            }
        }
        playbackRegionsAfterUncut.push([uncutStart, uncutEnd]);

        if (playbackRegionsAfterUncut.length === 0) {
            throw new Error('Uncutting playback regions resulted in no playback regions');
        }

        // save the new regions, sorted by start time
        playbackRegionsAfterUncut.sort((regionA, regionB) => regionA[0] - regionB[0]);
        return playbackRegionsAfterUncut;
    }

    /**
     * Calculates the duration of the specified playback regions.
     * Duration is calculated by summing the duration of all the regions.
     */
    static getDurationFromPlaybackRegions(
        regions: Readonly<Readonly<PlaybackRegion>[]> = [],
    ): number {
        return regions.reduce((duration, [start, end]) => {
            return duration + (end - start);
        }, 0);
    }

    static getStartTime(regions: Readonly<Readonly<PlaybackRegion>[]> = []): number {
        return regions.length > 0 ? regions[0][0] : 0;
    }

    /**
     * Sets the start time of playback regions via cutting/uncutting
     */
    static setStartTime(
        startTime: number,
        regions: Readonly<Readonly<PlaybackRegion>[]>,
        assetDuration: number,
    ): PlaybackRegion[] {
        const playbackRegionStartTime = this.getStartTime(regions);
        const playbackRegionEndTime = this.getEndTime(regions);

        if (startTime > playbackRegionEndTime && startTime < assetDuration) {
            // we're shifting the playback region to the right. to avoid confusion, we reduce to 1 playback region.
            return [
                [
                    startTime,
                    Math.min(
                        startTime + this.getDurationFromPlaybackRegions(regions),
                        assetDuration,
                    ),
                ],
            ] as PlaybackRegion[];
        }

        return startTime > playbackRegionStartTime
            ? this.cut(playbackRegionStartTime, startTime, assetDuration, regions)
            : this.uncut(startTime, playbackRegionStartTime, assetDuration, regions);
    }

    static getEndTime(regions: Readonly<Readonly<PlaybackRegion>[]> = []): number {
        return regions.length > 0 ? regions.at(-1)![1] : (null as unknown as number);
    }

    /**
     * Sets the end time of playback regions via cutting/uncutting
     */
    static setEndTime(
        endTime: number,
        regions: Readonly<Readonly<PlaybackRegion>[]>,
        assetDuration: number,
    ): PlaybackRegion[] {
        const playbackRegionEndTime = this.getEndTime(regions);
        const playbackRegionStartTime = this.getStartTime(regions);

        if (endTime < playbackRegionStartTime && endTime > 0) {
            // we're shifting the playback region to the right. to avoid confusion, we reduce to 1 playback region.
            return [
                [Math.max(endTime - this.getDurationFromPlaybackRegions(regions), 0), endTime],
            ] as PlaybackRegion[];
        }

        return endTime < playbackRegionEndTime
            ? this.cut(endTime, playbackRegionEndTime, assetDuration, regions)
            : this.uncut(playbackRegionEndTime, endTime, assetDuration, regions);
    }

    /**
     * Get the cut regions from the playback regions.
     */
    static getCutRegions(
        regions: Readonly<Readonly<PlaybackRegion>[]>,
        assetDuration: number,
    ): PlaybackRegion[] {
        const cutRegions: PlaybackRegion[] = [];
        let index = 0;
        for (const region of regions) {
            const [_, end] = region;
            // push the cut regions that are in between playback regions first
            if (index < regions.length - 1) {
                cutRegions.push([end, regions[index + 1][0]]);
            }
            index++;
        }

        const playbackStartTime = PlaybackRegions.getStartTime(regions);
        const playbackEndTime = PlaybackRegions.getEndTime(regions);
        if (playbackStartTime > 0) {
            cutRegions.unshift([0, playbackStartTime]);
        }
        if (playbackEndTime < assetDuration) {
            cutRegions.push([playbackEndTime, assetDuration]);
        }

        return cutRegions;
    }

    /**
     * Get the time with the cuts cut out. This is useful to convert time in asset to time in a luminary container (Scene, SceneGroup, etc.)
     */
    static assetTimeToPlaybackTime(
        regions: Readonly<Readonly<PlaybackRegion>[]>,
        assetDuration: number,
        time: number,
    ): number {
        const cutRegions = PlaybackRegions.getCutRegions(regions, assetDuration);

        // throw error if time is in a cut region
        cutRegions.forEach(([start, end]) => {
            // end time boundary is allowed because that should overlap with the start time of the next playback region
            if (time > start && time < end) {
                throw new Error(
                    'Cannot convert asset time to playback time: time is within a cut region',
                );
            }
        });

        const totalTimeCutOut = cutRegions.reduce((duration, [start, end]) => {
            // if the time is part of the cut region
            if (time > start) {
                return duration + (end - start);
            }
            return duration;
        }, 0);

        // return the time with the cuts cut out
        return time - totalTimeCutOut;
    }

    /**
     * Get the time with the cuts included. This is useful to convert time in a luminary container (Scene, SceneGroup, etc.) to time in asset.
     */
    static playbackTimeToAssetTime(
        regions: Readonly<Readonly<PlaybackRegion>[]>,
        assetDuration: number,
        time: number,
    ): number {
        const cutRegions = PlaybackRegions.getCutRegions(regions, assetDuration);

        // iterate the cut regions, keep track of total duration of cuts
        let totalCutDuration = 0;
        for (const [start, end] of cutRegions) {
            const cutRegionStartTimeAsPlaybackTime = start - totalCutDuration;
            // accumulate duration of cuts where the specified time elapses over the cut region
            if (
                /**
                 * if playback time elapses the cut region, translate the time with cut time included
                 * in case of overlap, we prioritize the later playback region's start time
                 * this means if time is on the border of the cut region we would include the cut duration
                 * see playback_regions_test for examples!
                 */
                cutRegionStartTimeAsPlaybackTime <= time
            ) {
                totalCutDuration += end - start;
            }
            // the cut region is later than our specified time, ignore
            else {
                break;
            }
        }

        // return the time inflated by the total cut duration
        return time + totalCutDuration;
    }

    /**
     * Check if the given time (in playback time) is in a future playback region or after the asset is done playing.
     */
    static playbackTimeIsInFuturePlaybackRegion(
        regions: Readonly<Readonly<PlaybackRegion>[]>,
        assetDuration: number,
        time: number,
        playbackRegionIdx: number,
    ) {
        assertNumberBetween(playbackRegionIdx, 0, regions.length - 1);
        const endTime = this.getEndTime(regions);
        const endTimeInPlaybackTime = PlaybackRegions.assetTimeToPlaybackTime(
            regions,
            assetDuration,
            endTime,
        );
        if (greaterThanOrEqualWithinEpsilon(time, endTimeInPlaybackTime, 1)) {
            // The time is after the asset is done playing
            return true;
        }
        const assetTime = PlaybackRegions.playbackTimeToAssetTime(regions, assetDuration, time);
        const [_, end] = regions[playbackRegionIdx];
        return assetTime >= end;
    }

    /**
     * Check if the given time (in playback time) is in the given playback region
     */
    static playbackTimeIsInPlaybackRegion(
        regions: Readonly<Readonly<PlaybackRegion>[]>,
        assetDuration: number,
        time: number,
        playbackRegionIdx: number,
    ) {
        assertNumberBetween(playbackRegionIdx, 0, regions.length - 1);
        const assetTime = PlaybackRegions.playbackTimeToAssetTime(regions, assetDuration, time);
        const [start, end] = regions[playbackRegionIdx];
        return assetTime >= start && assetTime <= end;
    }
}
